import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import { TimeSignalClassification } from 'graphql/types';
import { ProgressButton } from '../ProgressButton';

type TimerDialogProps = {
  open: boolean;
  onSecondaryAction: () => void | Promise<void>;
  onPrimaryAction: () => void | Promise<void>;
  onClose: () => void | Promise<void>;
  keepTimerRunningAction: () => void;
  selectedClassification?: TimeSignalClassification;
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(0)
  },
  title: {
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightRegular,
    fontSize: theme.spacing(2.5),
    lineHeight: 1.6,
    fontFamily: theme.typography.h6.fontFamily
  },
  messages: {
    color: theme.palette.text.primary
  },
  actionsWrapper: {
    padding: theme.spacing(2),
    justifyContent: 'center'
  },
  actionButton: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginLeft: theme.spacing(0.5),
    marginRight: theme.spacing(0.5)
  }
}));

const messages = {
  title: (
    <FormattedMessage
      key="note.inlineTimer.title"
      id="note.inlineTimer.title"
    />
  ),
  timerRunningMessage: (
    <FormattedMessage
      key="note.inlineTimer.timerRunningMessage"
      id="note.inlineTimer.timerRunningMessage"
    />
  ),
  acceptedMessage: (
    <FormattedMessage
      key="note.inlineTimer.acceptedMessage"
      id="note.inlineTimer.acceptedMessage"
    />
  ),
  cancel: <FormattedMessage id="note.inlineTimer.cancel" />,
  stopTimer: <FormattedMessage id="note.inlineTimer.stopTimer" />,
  keepTimerRunning: <FormattedMessage id="note.inlineTimer.keepTimerRunning" />,
  continue: <FormattedMessage id="note.inlineTimer.continue" />
};
export const TimerDialog: React.FC<TimerDialogProps> = ({
  open,
  onSecondaryAction,
  onPrimaryAction,
  onClose,
  keepTimerRunningAction,
  selectedClassification
}: TimerDialogProps) => {
  const classes = useStyles();

  return (
    <Dialog data-testid="timer-dialog" open={open} onClose={onClose}>
      <DialogTitle className={classes.title}>{messages.title}</DialogTitle>
      <DialogContent>
        <DialogContentText className={classes.messages}>
          {selectedClassification !== TimeSignalClassification.Accepted
            ? messages.timerRunningMessage
            : messages.acceptedMessage}
        </DialogContentText>
      </DialogContent>
      <DialogActions className={classes.actionsWrapper}>
        <Button
          data-testid="timer-dialog-btn-1"
          variant="text"
          onClick={onSecondaryAction}
          className={classes.actionButton}
        >
          <Typography color={'textSecondary'}>{messages.cancel}</Typography>
        </Button>
        {selectedClassification !== TimeSignalClassification.Accepted ? (
          <ProgressButton
            data-testid="timer-dialog-btn-2"
            variant="contained"
            color="secondary"
            loading={false}
            onClick={onPrimaryAction}
            className={classes.actionButton}
          >
            {messages.stopTimer}
          </ProgressButton>
        ) : (
          <></>
        )}
        <ProgressButton
          data-testid="timer-dialog-btn-3"
          variant="contained"
          color="primary"
          loading={false}
          onClick={
            selectedClassification === TimeSignalClassification.Accepted
              ? onPrimaryAction
              : keepTimerRunningAction
          }
          className={classes.actionButton}
        >
          {selectedClassification !== TimeSignalClassification.Accepted
            ? messages.keepTimerRunning
            : messages.continue}
        </ProgressButton>
      </DialogActions>
    </Dialog>
  );
};
